/**
 * Asset gallery picker. Opens from the image widget's "Browse" button
 * when the project has an `assetOrigin` configured. Lists objects from
 * the configured S3-compatible bucket, supports drag-drop upload, and
 * returns either the resolved public URL or just the key — caller
 * decides via `format`.
 *
 * Native <dialog> as everywhere else in this app: focus trap, Esc to
 * close, modal semantics, no overlay reimplementation.
 */

import { useCallback, useEffect, useRef, useState } from 'react';

import { type AssetClient, type AssetItem, AssetOriginError } from '../asset-origin.js';

import { Icon } from './Icon.js';
import { Spinner } from './Spinner.js';
import { toast } from './Toast.js';

import './AssetGallery.css';

interface Props {
  client: AssetClient;
  open: boolean;
  onClose: () => void;
  /** Called when the user picks an asset. */
  onSelect: (value: string, item: AssetItem) => void;
  /**
   * What gets passed to `onSelect`. `'url'` returns the resolved
   * absolute public URL; `'key'` returns the bare object key.
   * Defaults to `'url'`.
   */
  format?: 'url' | 'key';
}

export function AssetGallery({
  client,
  open,
  onClose,
  onSelect,
  format = 'url',
}: Props): JSX.Element {
  const dialogRef = useRef<HTMLDialogElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [items, setItems] = useState<AssetItem[]>([]);
  const [nextToken, setNextToken] = useState<string | undefined>(undefined);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [dragActive, setDragActive] = useState(false);

  const refresh = useCallback(async (): Promise<void> => {
    setLoading(true);
    setError(null);
    try {
      const result = await client.list({ maxKeys: 100 });
      setItems(result.items);
      setNextToken(result.nextToken);
    } catch (err) {
      setError(toMessage(err));
      setItems([]);
      setNextToken(undefined);
    } finally {
      setLoading(false);
    }
  }, [client]);

  const loadMore = async (): Promise<void> => {
    if (!nextToken || loading) return;
    setLoading(true);
    try {
      const result = await client.list({
        maxKeys: 100,
        continuationToken: nextToken,
      });
      setItems((prev) => [...prev, ...result.items]);
      setNextToken(result.nextToken);
    } catch (err) {
      setError(toMessage(err));
    } finally {
      setLoading(false);
    }
  };

  // Open/close native dialog in response to prop. showModal() gives us
  // focus trap + modal semantics for free.
  useEffect(() => {
    const d = dialogRef.current;
    if (!d) return;
    if (open && !d.open) d.showModal();
    else if (!open && d.open) d.close();
  }, [open]);

  // Sync native close (Esc / backdrop) back to React state.
  useEffect(() => {
    const d = dialogRef.current;
    if (!d) return;
    const handler = (): void => onClose();
    d.addEventListener('close', handler);
    return () => d.removeEventListener('close', handler);
  }, [onClose]);

  // Refresh whenever the dialog opens. Don't keep stale lists from a
  // previous opening — the user might've uploaded outside Bliksys.
  useEffect(() => {
    if (open) {
      void refresh();
      setSearch('');
    }
  }, [open, refresh]);

  const handleFiles = async (files: FileList | File[]): Promise<void> => {
    const list = Array.from(files);
    if (list.length === 0) return;
    setUploading(true);
    setError(null);
    try {
      for (const file of list) {
        await client.upload(file.name, file);
      }
      toast(
        list.length === 1
          ? `Uploaded ${list[0]?.name ?? 'file'}.`
          : `Uploaded ${list.length} files.`,
        'success',
      );
      await refresh();
    } catch (err) {
      const message = toMessage(err);
      setError(message);
      toast(`Upload failed: ${message}`, 'error');
    } finally {
      setUploading(false);
    }
  };

  const filtered = search
    ? items.filter((item) => item.key.toLowerCase().includes(search.toLowerCase()))
    : items;

  return (
    <dialog ref={dialogRef} className="asset-gallery-dialog" aria-labelledby="ag-title">
      {/*
        Drag-and-drop is a mouse-only enhancement on top of the visible
        Upload button — keyboard users have a fully-equivalent path via
        the Upload button + native file picker. The wrapper exists only
        to host the drop targets.
      */}
      {/* eslint-disable-next-line jsx-a11y/no-noninteractive-element-interactions */}
      <div
        className={'asset-gallery' + (dragActive ? ' is-drag-active' : '')}
        role="region"
        aria-label="Asset picker"
        onDragEnter={(e) => {
          e.preventDefault();
          setDragActive(true);
        }}
        onDragOver={(e) => {
          e.preventDefault();
        }}
        onDragLeave={(e) => {
          // Only un-flag when leaving the gallery itself, not children.
          if (e.target === e.currentTarget) setDragActive(false);
        }}
        onDrop={(e) => {
          e.preventDefault();
          setDragActive(false);
          if (e.dataTransfer.files.length > 0) {
            void handleFiles(e.dataTransfer.files);
          }
        }}
      >
        <header className="asset-gallery-header">
          <h2 id="ag-title" className="asset-gallery-title">
            Select an asset
          </h2>
          <button
            type="button"
            className="asset-gallery-close"
            onClick={onClose}
            aria-label="Close"
          >
            ×
          </button>
        </header>

        <div className="asset-gallery-toolbar">
          <input
            type="search"
            className="input"
            placeholder="Filter by name…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            aria-label="Filter assets"
          />
          <button
            type="button"
            className="landing-button-text"
            onClick={() => fileInputRef.current?.click()}
            disabled={uploading}
          >
            <Icon name="import" size={14} /> {uploading ? 'Uploading…' : 'Upload'}
          </button>
          <input
            ref={fileInputRef}
            type="file"
            multiple
            className="sr-only"
            onChange={(e) => {
              if (e.target.files) {
                void handleFiles(e.target.files);
                e.target.value = '';
              }
            }}
          />
          <button
            type="button"
            className="landing-button-text"
            onClick={() => void refresh()}
            disabled={loading}
          >
            Refresh
          </button>
        </div>

        {error && (
          <p className="asset-gallery-error" role="alert">
            {error}
          </p>
        )}

        <div className="asset-gallery-body">
          {loading && items.length === 0 ? (
            <div className="asset-gallery-empty">
              <Spinner label="Loading…" />
            </div>
          ) : filtered.length === 0 ? (
            <p className="asset-gallery-empty">
              {items.length === 0
                ? 'No assets yet. Drop files here or click Upload.'
                : 'No assets match your filter.'}
            </p>
          ) : (
            <ul className="asset-gallery-grid" aria-label="Assets">
              {filtered.map((item) => (
                <AssetTile
                  key={item.key}
                  item={item}
                  onSelect={() => {
                    onSelect(format === 'url' ? item.url : item.key, item);
                    onClose();
                  }}
                />
              ))}
            </ul>
          )}
        </div>

        <footer className="asset-gallery-footer">
          {nextToken && (
            <button
              type="button"
              className="landing-button-text"
              onClick={() => void loadMore()}
              disabled={loading}
            >
              Load more
            </button>
          )}
          <span className="asset-gallery-footer-spacer" />
          <button type="button" className="landing-button-text" onClick={onClose}>
            Cancel
          </button>
        </footer>
      </div>
    </dialog>
  );
}

function AssetTile({ item, onSelect }: { item: AssetItem; onSelect: () => void }): JSX.Element {
  const filename = item.key.split('/').pop() ?? item.key;
  const looksLikeImage = /\.(png|jpe?g|gif|webp|svg|avif|bmp|ico)$/i.test(filename);
  return (
    <li className="asset-gallery-tile">
      <button
        type="button"
        className="asset-gallery-tile-button"
        onClick={onSelect}
        title={item.key}
      >
        <span className="asset-gallery-tile-thumb">
          {looksLikeImage ? (
            <img
              src={item.url}
              alt=""
              loading="lazy"
              onError={(e) => {
                // Hide broken thumbnails — keeps the grid tidy when
                // the bucket isn't publicly readable yet.
                e.currentTarget.style.display = 'none';
              }}
            />
          ) : (
            <Icon name="file-document" size={28} />
          )}
        </span>
        <span className="asset-gallery-tile-name">{filename}</span>
        <span className="asset-gallery-tile-meta">{formatSize(item.size)}</span>
      </button>
    </li>
  );
}

function formatSize(bytes: number): string {
  if (bytes < 0) return '';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
}

function toMessage(err: unknown): string {
  if (err instanceof AssetOriginError) return err.message;
  if (err instanceof Error) return err.message;
  return 'Unknown error.';
}
